// src/components/SecureSignalsHealthPanel.jsx
// Presentational "SECURE SIGNALS" health rollup — peer of the BID HEALTH and
// OMID VIEWABILITY panels in the lifecycle view. Shows, per declared secure-
// signal adapter, how often it actually reaches GAM via a3p (coverage rate),
// with drill-in to the specific GAM requests that are missing it.
//
// `health` is the object returned by buildSecureSignalsHealth(). `onJumpTo`, if
// provided, is called with a row key to focus the offending request.

const C  = { fail: "#A32D2D", warn: "#854F0B", pass: "#1D9E75", info: "#6b7280" };
const BG = { fail: "var(--error-bg)", warn: "var(--warn-bg)", pass: "var(--success-bg)", info: "var(--surface)" };
const ICON = { fail: "✗", warn: "⚠", pass: "✓", info: "•" };

// Compact label for a GAM request: last two ad-unit segments, else the raw id.
const shortReq = (iu) => {
    if (!iu) return "request";
    if (iu.startsWith("/")) {
        const segs = iu.split("/").filter(Boolean);
        return segs.slice(-2).join("/") || iu;
    }
    return iu;
};

export default function SecureSignalsHealthPanel({ health, onJumpTo }) {
    if (!health || !Array.isArray(health.adapters) || health.adapters.length === 0) {
        return null;
    }

    return (
        <div style={{ marginBottom: 14, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, color: "var(--muted)", padding: "6px 12px", background: "var(--surface)", borderBottom: "1px solid var(--line)" }}>
                SECURE SIGNALS · a3p coverage across {health.total} GAM request{health.total !== 1 ? "s" : ""}
            </div>
            {health.adapters.map((a) => {
                const color = C[a.severity] || C.info;
                const bg = BG[a.severity] || BG.info;
                const missing = a.missing || [];
                const MAX_CHIPS = 4;
                return (
                    <div key={a.name}
                        style={{ display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 6, columnGap: 12, padding: "8px 12px", fontSize: 12, background: bg, borderTop: "1px solid var(--line)" }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                        <span style={{ fontWeight: 600, minWidth: 160, whiteSpace: "nowrap" }}>{a.name}</span>
                        <span style={{ color: "var(--muted)", whiteSpace: "nowrap" }}>
                            declared · reaching <b style={{ color }}>{a.reaching}/{a.total}</b> ({a.pct}%)
                        </span>

                        {/* Name the specific GAM request(s) missing this signal — each chip
                            jumps to that request's Validate tab (the authoritative detail). */}
                        {a.severity !== "pass" && missing.length > 0 && (
                            <span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                                <span style={{ color: "var(--muted)" }}>missing on:</span>
                                {missing.slice(0, MAX_CHIPS).map((m, i) => {
                                    const clickable = m.key != null && typeof onJumpTo === "function";
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            onClick={clickable ? () => onJumpTo(m.key) : undefined}
                                            disabled={!clickable}
                                            title={clickable ? `Open ${m.iu} → Validate tab` : m.iu}
                                            style={{
                                                fontSize: 11, color, background: "var(--card, #fff)",
                                                border: `1px solid ${color}`, borderRadius: 6, padding: "1px 7px",
                                                cursor: clickable ? "pointer" : "default", fontWeight: 600,
                                                maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }}
                                        >
                                            {shortReq(m.iu)}{clickable ? " →" : ""}
                                        </button>
                                    );
                                })}
                                {missing.length > MAX_CHIPS && (
                                    <span style={{ color, fontWeight: 600 }} title={missing.map((m) => m.iu).join("\n")}>
                                        +{missing.length - MAX_CHIPS} more
                                    </span>
                                )}
                            </span>
                        )}

                        <span style={{ marginLeft: "auto", color, fontWeight: 700, whiteSpace: "nowrap" }}>
                            {ICON[a.severity]} {a.severity === "pass" ? "reaching" : a.severity === "warn" ? "partial" : "not reaching"}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
